import { Box, Container, Typography, TextField, Button } from '@mui/material';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../utils/redux';
import {
  setProfileInfo,
  setCurrentStep,
} from '../../../utils/redux/slices/signupSlice';
import { profileBusinessInfoSchema } from '../../../schemas';
import theme from '../../../theme';
import { useState, useEffect } from 'react';

export default function ProfileBusinessInfo() {
  const dispatch = useDispatch();
  const { profileInfo } = useSelector((state: RootState) => state.signup);
  const [isFormValid, setIsFormValid] = useState(false);

  const formik = useFormik({
    initialValues: {
      fullName: profileInfo.fullName || '',
      businessName: profileInfo.businessName || '',
    },
    validationSchema: profileBusinessInfoSchema,
    onSubmit: values => {
      // Save form data to Redux store
      dispatch(
        setProfileInfo({
          fullName: values.fullName,
          businessName: values.businessName,
        })
      );

      // Move to next step
      dispatch(setCurrentStep(3));
    },
  });

  // Check if form is valid whenever values or errors change
  useEffect(() => {
    const { fullName, businessName } = formik.values;
    const hasValues = fullName.trim() !== '' && businessName.trim() !== '';
    const hasNoErrors = Object.keys(formik.errors).length === 0;

    setIsFormValid(hasValues && hasNoErrors);
  }, [formik.values, formik.errors]);

  const handleBack = () => {
    dispatch(setCurrentStep(1));
  };

  return (
    <Box
      sx={{
        flex: '1 1 50%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 2, md: 2, lg: 2 },
        bgcolor: 'white',
        overflow: 'auto',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            px: '24px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: '#2B2D3F',
              fontSize: '24px',
            }}
          >
            Your Profile & Business Information
          </Typography>
          <Typography
            sx={{
              color: '#6B6F80',
              fontSize: '14px',
            }}
          >
            Provide essential details about yourself and your business
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mb: 2, px: '24px' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
              Full Name
            </Typography>
            <TextField
              fullWidth
              name="fullName"
              placeholder="Full Name"
              size="small"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              helperText={formik.touched.fullName && formik.errors.fullName}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2, px: '24px' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
              Business Name
            </Typography>
            <TextField
              fullWidth
              name="businessName"
              placeholder="Business Name"
              size="small"
              value={formik.values.businessName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessName &&
                Boolean(formik.errors.businessName)
              }
              helperText={
                formik.touched.businessName && formik.errors.businessName
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  width: '450px',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              // justifyContent: 'space-between',
              px: '24px',
              mt: 4,
              gap: '10px',
              width: '100%',
            }}
          >
            <Button
              type="button"
              onClick={handleBack}
              sx={{
                py: 1.5,
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '14px',
                height: '34px',
                width: '10%',
              }}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid}
              sx={{
                py: 1.5,
                bgcolor: theme.palette.primary.main,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '14px',
                height: '34px',
                width: '100%',
                '&.Mui-disabled': {
                  bgcolor: '#E0E0E0',
                  color: '#9E9E9E',
                },
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  boxShadow: 'none',
                  scale: '1.02',
                },
              }}
            >
              Continue
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
}
